import { Popover } from '@headlessui/react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';
import ClipsList from '../ClipsListPage/ClipsList';
import ContentDialog from '../Sessions/uiComponents/ContentDialog/ContentDialog';
import RenameTitleModal from '../Sessions/AssetsTable/RenameTitleModal';
import SourceSelectionDialog from '../Sessions/RecentEvents/AiClips/SourceSelectionDialog';
import { CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME } from './constants';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useClipList } from '@/hooks/useClipList';
import { toggleListCollapse } from '@/stores/userPreferencesStore';
import { useClipListPopover } from '@/hooks/useClipListPopover';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { addClipToTimelineStore, stopAddClipToTimeline } from '@/stores/addClipToTimelineStore';
import useAnalytics from '@/hooks/useAnalytics';
import { getClipCompositeSegments } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineSegments/SegmentGap/clipSegmentImport';
import { showErrorToast } from '@/libs/toast/toast';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';

const ClipListPopover = () => {
  const popoverRef = useRef<HTMLElement>(null);
  const { trackAddClipInserted } = useAnalytics();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isMultiSelectEnabled = featureFlags[FeatureFlagKeys.Use_CL_Multi_Select_Assets];

  const { isClipListPopoverOpen, closeDialog } = useClipListPopover();
  const {
    isSelecting: isSegmentInsertMode,
    onInsert,
    targetLayout,
    sourceClipId
  } = useSyncExternalStore(addClipToTimelineStore.subscribe, addClipToTimelineStore.getSnapshot);
  const [pendingInsertClips, setPendingInsertClips] = useState<Clip[] | null>(null);

  const insertClips = useCallback(
    (selectedClips: Clip[]) => {
      // Flatten all clips' composites into one array so a single ADD_CLIP_SEGMENTS call
      // preserves selection order. Calling onInsert per clip would re-anchor each batch
      // at the same previousSegment, reversing the clips' order on the timeline.
      const allSegments: Segment[] = [];
      for (const clip of selectedClips) {
        const segments = getClipCompositeSegments(clip);
        if (!segments.length) {
          showErrorToast(`"${clip.title}" does not have a video source available.`);
          continue;
        }
        allSegments.push(...segments);
      }
      if (allSegments.length) {
        onInsert?.(allSegments);
        trackAddClipInserted({ sourceClipId: sourceClipId ?? '', clipCount: selectedClips.length });
      }
      stopAddClipToTimeline();
      EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
    },
    [onInsert, sourceClipId, trackAddClipInserted]
  );

  const handleInsertClips = useCallback(
    (selectedClips: Clip[]) => {
      // The timeline renders one layout throughout, so an imported clip gets re-laid-out to match
      // the target. That is lossy in the audiogram direction — per-scene framing collapses to a
      // single waveform scene — so confirm first. Non-audiogram targets re-map scene layouts
      // without dropping anything and need no warning.
      const isTargetAudiogram = !!targetLayout && isLayoutAudiogram(targetLayout);
      const hasMismatchedLayout = selectedClips.some(
        clip => isLayoutAudiogram(clip.asset_metadata.layout) !== isTargetAudiogram
      );
      if (hasMismatchedLayout) {
        setPendingInsertClips(selectedClips);
        return;
      }
      insertClips(selectedClips);
    },
    [targetLayout, insertClips]
  );

  const handleConfirmLayoutChange = useCallback(() => {
    const clipsToInsert = pendingInsertClips ?? [];
    setPendingInsertClips(null);
    insertClips(clipsToInsert);
  }, [pendingInsertClips, insertClips]);

  const handleCancelSegmentInsert = useCallback(() => {
    setPendingInsertClips(null);
    stopAddClipToTimeline();
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
  }, []);

  const {
    allVisibleClips,
    canGenerateClipsState,
    onGenerateMoreClips,
    handleOnDelete,
    setRenamingClip,
    onGenerateSocialPost,
    duplicateClip,
    renamingClip,
    onSaveNewTitle,
    closeRenamingClipDialog,
    isSourceSelectionDialogOpen,
    closeSourceSelectionDialog,
    relevantSourceList,
    loadingMoreClips,
    generatingMoreClip,
    handleSelectedSource
  } = useClipList({ setFirstClipsAsDefault: false });

  const isSourceSelectionDialogOpenRef = useRef(isSourceSelectionDialogOpen);
  useEffect(() => {
    isSourceSelectionDialogOpenRef.current = isSourceSelectionDialogOpen;
  }, [isSourceSelectionDialogOpen]);

  useEffect(() => {
    if (isClipListPopoverOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const isClickOutsidePopover = !popoverRef.current?.contains(event.target as Node);
        const isClickAssumedInsidePopover = (event.target as HTMLElement).closest(
          `.${CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}`
        );
        if (
          popoverRef.current &&
          isClickOutsidePopover &&
          !isClickAssumedInsidePopover &&
          !isSourceSelectionDialogOpenRef.current
        ) {
          // Clear insert mode alongside closing, or the store would stay armed with a stale
          // handler and the panel would reopen as a picker next time.
          stopAddClipToTimeline();
          EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isClipListPopoverOpen, closeDialog]);

  if (!isClipListPopoverOpen) {
    return <></>;
  }

  return (
    <Popover
      ref={popoverRef}
      className={`absolute left-20 z-[100] flex w-[28rem] flex-col rounded-2xl border`}
      style={{
        top: '8px',
        // Insert mode always needs the full-height panel so its footer stays reachable.
        height: isMultiSelectEnabled || isSegmentInsertMode ? 'calc(100% - 15px)' : 'calc(100% - 8px - 128px)'
      }}
    >
      <div className="flex h-full overflow-hidden">
        <div className="flex size-full overflow-visible transition-all duration-[400ms] ease-in-out">
          <ClipsList
            clips={
              isSegmentInsertMode && sourceClipId ? allVisibleClips.filter(c => c.id !== sourceClipId) : allVisibleClips
            }
            canGenerate={canGenerateClipsState}
            isCollapsed={false}
            isHidden={false}
            generateClips={onGenerateMoreClips}
            onDelete={handleOnDelete}
            onRenameClip={setRenamingClip}
            onGenerateSocialPost={onGenerateSocialPost}
            onDuplicateClip={duplicateClip}
            toggleCollapse={toggleListCollapse}
            hideCollapseButton
            animateClipList={false}
            isInPopover
            isSegmentInsertMode={isSegmentInsertMode}
            sourceClipId={sourceClipId ?? undefined}
            onInsertClips={handleInsertClips}
            onCancelSegmentInsert={handleCancelSegmentInsert}
          />
        </div>
      </div>
      {renamingClip ? (
        <ContentDialog
          className={CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}
          isOpen={!!renamingClip}
          setIsOpen={closeRenamingClipDialog}
          title="Rename"
          size="xsmall"
        >
          <RenameTitleModal
            existingTitle={renamingClip.title}
            onSubmit={onSaveNewTitle}
            onClose={closeRenamingClipDialog}
          />
        </ContentDialog>
      ) : null}
      <SourceSelectionDialog
        isOpen={isSourceSelectionDialogOpen}
        isLoading={loadingMoreClips}
        isSearching={generatingMoreClip}
        closeDialog={closeSourceSelectionDialog}
        sourceList={relevantSourceList.contents}
        onSourceChanged={handleSelectedSource}
      />
      <ConfirmationDialog
        isOpen={!!pendingInsertClips}
        title="Layout will be updated"
        content={
          !!targetLayout && isLayoutAudiogram(targetLayout)
            ? 'The selected clip uses a non-Audiogram layout. Adding it will update its layout to match the existing clip.'
            : 'The selected clip uses an Audiogram layout. Adding it will update its layout to match the existing clip.'
        }
        confirmLabel="Continue"
        cancelLabel="Cancel"
        actionsLayout="end"
        confirmTrackingId="add-clip-layout-warning-continue"
        cancelTrackingId="add-clip-layout-warning-cancel"
        onClose={() => setPendingInsertClips(null)}
        onConfirm={handleConfirmLayoutChange}
      />
    </Popover>
  );
};

export default ClipListPopover;
